import React, { FunctionComponent, useRef } from 'react';
import { Object3D, Raycaster, Intersection, Group, Object3DEventMap, FrontSide, DoubleSide } from 'three';
import { hasEntityTapAction, transitionAdjustedRenderOrder } from '../../utils';
import { useDataStore, useSceneStore, useUserStore } from '../../zustand';
import EntityLevelFadeGroup from '../fadeGroups/EntityLevelFadeGroup';
import { IText, ITextReactProps } from '@r3f-component-data-structure';
import { EntityErrorBoundary } from '@r3f/components/boundaries';
import {
	useGetAdjustedFontSize,
	useGetAdjustedMemoSpatialProps,
	useGetEntityIsHidden,
	useGetSubscribedSceneTransitionCategoryRef,
	useGetSubscribedSceneTransitionPhaseRef,
	useHocPointerDown,
	useOnEntityTestClick,
	useResetEntityTransformsOnPlacement,
} from './hocCustomHooks';
import { useInteraction } from '../TrackerGroups/WorldXRTracker/webxr-hooks';
import { useEnableObjectLayer, useGetSceneIndexBySceneId } from './hocCustomHooks';
import { useGrabAndGoAnimation } from '../TrackerGroups/ImageTrackerWrapper/hooks';
interface ITextWrapped {
	id: string;
	sceneId: string;
	screenRelative?: boolean;
	raycast?: (_: Raycaster, intersects: Intersection<Object3D<Object3DEventMap>>[]) => void;
}

const withTextBehaviour = (WrappedText: FunctionComponent<ITextReactProps>): FunctionComponent<ITextWrapped> => {
	const FuncComp: FunctionComponent<ITextWrapped> = ({ id, sceneId, screenRelative = false, raycast }) => {
		const pointerIsDownRef = useRef(false);
		const pointerGroupRef = useRef<Group>(null)
		const textEntity = useDataStore(({ componentsById }) => componentsById?.[id] as IText) || {};
		const sceneTransCatRef = useGetSubscribedSceneTransitionCategoryRef(sceneId);
		const shadowsEnabled = useDataStore(({ shadowsEnabled }) => shadowsEnabled ?? true);
		// ! Don't remove this line - it's a quick-fix to deliberately cause a re-render to keep render order in sync with Video components
		const sceneTransitionCategory = useSceneStore(({ transitionCategory }) => transitionCategory[sceneId]);
		const sceneTransPhaseRef = useGetSubscribedSceneTransitionPhaseRef(sceneId);
		const entityIsHidden = useGetEntityIsHidden(id) && screenRelative;
		const sceneIndex = useGetSceneIndexBySceneId(sceneId);
		const isGrabAndGoActive = useUserStore(({ isGrabNGoActive }) => isGrabNGoActive);

		useOnEntityTestClick({ sceneId, entity: textEntity, sceneTransCatRef, sceneTransPhaseRef });

		useEnableObjectLayer({ object3d: pointerGroupRef, enabled: hasEntityTapAction(textEntity), layer: typeof sceneIndex === 'undefined' ? undefined : sceneIndex + 1 });

		// Ensures that any transforms due to previous animation presets are cleared out
		useResetEntityTransformsOnPlacement(id);

		// Get entity values
		const fontSize = useGetAdjustedFontSize(textEntity, screenRelative);
		const renderOrder = transitionAdjustedRenderOrder(sceneTransCatRef, (textEntity.renderOrder ?? 0) * 20);
		const { position, scale, rotation, zeroTuple3 } = useGetAdjustedMemoSpatialProps(textEntity, screenRelative);
		const { text, fontRgba, opacity, color, fontFamily, textAlignment, curvature, castShadow = false, receiveShadow = false, isSnappedToTarget = false, depthTest = true, billboard } = textEntity;

		const onPointerDown = useHocPointerDown({ pointerIsDownRef, sceneTransCatRef, sceneTransPhaseRef, sceneId, entity: textEntity, screenRelative });
		useInteraction(pointerGroupRef, 'onSelect', e => {
			onPointerDown(e.intersections)
			pointerIsDownRef.current = false;
		})

		return (
			<group name="ENTITY_VISIBLE_GROUP" visible={!entityIsHidden}>
				<EntityLevelFadeGroup name={`fadeGroup-${id}`} sceneId={sceneId} entityId={id} position={zeroTuple3} screenRelative={screenRelative}>
					<EntityErrorBoundary renderOrder={renderOrder} scale={scale} rotation={rotation} position={position}>
						<group name={`toggleVisibilityGroup-${id}`}>
							<group ref={pointerGroupRef} name={`sceneId_${sceneId}`} onPointerUp={() => (pointerIsDownRef.current = false)}>
								<WrappedText
									id={id}
									renderOrder={renderOrder}
									rotation={rotation}
									position={position}
									scale={scale}
									text={text}
									fontRgba={fontRgba}
									curvature={curvature}
									color={color}
									fontSize={fontSize}
									fontFamily={fontFamily}
									opacity={opacity}
									side={isSnappedToTarget ? FrontSide :  DoubleSide}
									textAlignment={textAlignment}
									castShadow={shadowsEnabled && castShadow}
									receiveShadow={shadowsEnabled && receiveShadow}
									raycast={raycast}
									onPointerDown={(e) => onPointerDown(e.intersections)}
									depthTest={depthTest}
									animationTransformGroupPrefix='transformGroup-'
									billboard={billboard}
									isCameraOrbiting={isGrabAndGoActive}
									rotateNoBillboardOuterGroup={false}
								/>
							</group>
						</group>
					</EntityErrorBoundary>
				</EntityLevelFadeGroup>
			</group>
		);
	};
	return FuncComp;
};

export default withTextBehaviour;
